import * as React from 'react';
import { PlanningView } from './views/PlanningView';
import { ClientsView } from './views/ClientsView';
import { ClientDashboardView } from './views/ClientDashboardView';
import { DepotsView } from './depots/DepotsView';
import { ActionsDashboard } from './actions/ActionsDashboard';
import { AdminPanel } from './admin/AdminPanel';
import { useAuth, useAuthProvider } from '../core/context/ProvidersContext';
import styles from './App.module.scss';

export interface IAppProps {}

type TabKey = 'actions' | 'planning' | 'depots' | 'restrictions' | 'clients' | 'admin';

const ROLE_LABELS: Record<string, string> = {
  admin: 'admin',
  rm: 'RM interne',
  client: 'client',
};

export const App: React.FC<IAppProps> = () => {
  const { user, hasRole, isStaff, isExternalClient, refresh } = useAuth();
  const authProvider = useAuthProvider();
  const isAdmin = hasRole('admin');

  const initialTab: TabKey = isExternalClient ? 'clients' : 'actions';
  const [activeTab, setActiveTab] = React.useState<TabKey>(initialTab);
  const [animKey, setAnimKey] = React.useState(0);

  // If the role changes at runtime (post-login / re-login), snap back to a tab
  // the user is allowed to see.
  React.useEffect(() => {
    if (isExternalClient && activeTab !== 'clients') {
      setActiveTab('clients');
      setAnimKey(k => k + 1);
    }
  }, [isExternalClient, activeTab]);

  const handleTabChange = (tab: TabKey): void => {
    if (tab !== activeTab) {
      setActiveTab(tab);
      setAnimKey(k => k + 1);
    }
  };

  const handleLogout = async (): Promise<void> => {
    if (authProvider.logout) {
      await authProvider.logout();
      refresh();
      window.location.reload();
    }
  };

  return (
    <div className={styles.app}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.logo}>RM</div>
          <div>
            <div className={styles.title}>Gestion des Bacs</div>
            <div className={styles.subtitle}>Suivi disponibilité &amp; restrictions</div>
          </div>
        </div>
        {user && (
          <div className={styles.headerRight}>
            <span className={styles.userBadge}>
              {user.displayName}
              <span className={styles.userRole}>{ROLE_LABELS[user.role] || user.role}</span>
            </span>
            {authProvider.supportsLogin && (
              <button className={styles.logoutBtn} onClick={handleLogout}>
                Déconnexion
              </button>
            )}
          </div>
        )}
      </div>

      <div className={styles.tabs}>
        {isStaff && (
          <>
            <button
              className={activeTab === 'actions' ? styles.tabActive : styles.tab}
              onClick={() => handleTabChange('actions')}
            >
              Actions
            </button>
            <button
              className={activeTab === 'planning' ? styles.tabActive : styles.tab}
              onClick={() => handleTabChange('planning')}
            >
              Planning
            </button>
            <button
              className={activeTab === 'depots' ? styles.tabActive : styles.tab}
              onClick={() => handleTabChange('depots')}
            >
              Dépôts
            </button>
            <button
              className={activeTab === 'restrictions' ? styles.tabActive : styles.tab}
              onClick={() => handleTabChange('restrictions')}
            >
              Restrictions
            </button>
          </>
        )}
        <button
          className={activeTab === 'clients' ? styles.tabActive : styles.tab}
          onClick={() => handleTabChange('clients')}
        >
          {isExternalClient ? 'Mon tableau de bord' : 'Clients'}
        </button>
        {isAdmin && (
          <button
            className={activeTab === 'admin' ? styles.tabActive : styles.tab}
            onClick={() => handleTabChange('admin')}
          >
            Administration
          </button>
        )}
      </div>

      <div className={styles.content}>
        <div key={animKey} className={styles.tabContent}>
          {activeTab === 'actions' && isStaff && <ActionsDashboard />}
          {activeTab === 'planning' && isStaff && <PlanningView />}
          {activeTab === 'depots' && isStaff && <DepotsView />}
          {activeTab === 'restrictions' && isStaff && <ClientsView />}
          {activeTab === 'clients' && <ClientDashboardView />}
          {activeTab === 'admin' && isAdmin && <AdminPanel />}
        </div>
      </div>
    </div>
  );
};
